import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { CheckCircle2, Loader2 } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { ChatAvatar, MARQUE } from "@dozka/ui";
import { Button, EnTeteDePanneau, Lignes, Ligne } from "@/shared/ui";

import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { ENTETE_ECRAN } from "../enteteEcran";

interface DoneStepProps {
  accountId: Id<"accounts">;
}

/**
 * Étape 2 (finale) du wizard agency — récap + sortie vers le cockpit.
 *
 * À la sortie : mark `accounts.onboardingCompletedAt`. OrgGate route alors
 * vers `CockpitShell`, qui rend immédiatement le `CreatorOnboardingWizard`
 * en overlay (parce que `creatorsCount === 0`). L'user passe donc sans
 * transition de l'onboarding agence à l'onboarding du premier créateur —
 * où il lui sera demandé de connecter Dropp avant de créer son créateur.
 *
 * Pas de bouton "skip" : à ce stade, l'organisation est créée.
 */
export function DoneStep({ accountId }: DoneStepProps) {
  const { t } = useTranslation();
  const accounts = useQuery(api.accounts.listMine);
  const account = accounts?.find((a) => a._id === accountId);
  const markCompleted = useMutation(api.onboarding.markOnboardingCompleted);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleContinue() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await markCompleted({ accountId });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col items-center gap-4 text-center">
        {account?.logoUrl ? (
          <ChatAvatar src={account.logoUrl} seed={account.name ?? "organisation"} size={64} />
        ) : (
          <img src={MARQUE.iconeConnexion} alt="" aria-hidden="true" className="h-16 w-16" />
        )}
        <EnTeteDePanneau
          titre={t("auth.onboarding.done.title")}
          sousTitre={t("auth.onboarding.done.subtitle", { name: account?.name ?? "" })}
          className={ENTETE_ECRAN}
        />
      </div>

      <Lignes>
        <Ligne
          etiquette={t("auth.onboarding.done.recap.agency")}
          valeur={
            <span className="flex items-center gap-2 text-[15px] text-fg">
              <CheckCircle2 className="h-4 w-4 text-valide" />
              {account?.name ?? "—"}
            </span>
          }
        />
      </Lignes>

      <p className="mt-5 text-[13px] leading-relaxed text-muted">{t("auth.onboarding.done.nextStep")}</p>

      {error && (
        <p className="mt-3 text-[13px] text-erreur" role="alert">
          {error}
        </p>
      )}

      <Button
        variant="accent"
        size="xl"
        onClick={handleContinue}
        disabled={submitting}
        icon={submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
        className="mt-6 w-full"
      >
        {submitting ? t("auth.onboarding.done.continuing") : t("auth.onboarding.done.continue")}
      </Button>
    </div>
  );
}
